import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import KNCountryFlag from 'components/KNCountryFlag/KNCountryFlag'
import { isEqual, isSameDay, isThisYear, parseISO } from 'date-fns'
import { zonedDate } from 'global/helpers/dates'
import { cloneElement, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export interface CarrierQuotesHeaderProps {
  type: 'pickup' | 'delivery'
  windowStartDate: string
  windowEndDate: string
  country: string
  city: string
  zipCode: string
  icon?: ReactElement
}

const CarrierQuotesHeader = ({ type, windowStartDate, windowEndDate, country, city, zipCode, icon }: CarrierQuotesHeaderProps) => {
  const { t } = useTranslation()

  const getHeaderText = () => {
    const parsedWindowStart = parseISO(windowStartDate)
    const parsedWindowEnd = parseISO(windowEndDate)

    const fullDateFormat = isThisYear(parsedWindowStart) ? 'full_no_year' : 'full'
    const mediumDateFormat = isThisYear(parsedWindowStart) ? 'medium_no_year' : 'medium'

    if (isEqual(parsedWindowStart, parsedWindowEnd)) {
      return t(`screens.cs.carrier_quotes.quote_accordion.${type}_single_date`, {
        windowStart: zonedDate(windowStartDate, fullDateFormat),
      })
    } else if (isSameDay(parsedWindowStart, parsedWindowEnd)) {
      return t(`screens.cs.carrier_quotes.quote_accordion.${type}_on_same_day`, {
        date: zonedDate(windowStartDate, mediumDateFormat),
        hour1: zonedDate(windowStartDate, 'time'),
        hour2: zonedDate(windowEndDate, 'time'),
      })
    } else {
      return t(`screens.cs.carrier_quotes.quote_accordion.${type}_window`, {
        windowStart: zonedDate(windowStartDate, fullDateFormat),
        windowEnd: zonedDate(windowEndDate, fullDateFormat),
      })
    }
  }

  return (
    <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
      <Stack direction="row">
        {icon &&
          cloneElement(icon, {
            sx: {
              color: 'primary.main',
              marginRight: 1,
            },
          })}
        <Typography variant="p2" color="primary.main">
          {getHeaderText()}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center">
        <KNCountryFlag countryCode={country} sx={{ width: '1.5rem', height: '1rem', marginRight: 1 }} />
        <Typography variant="p3" color="info.focus">
          {city} {country} {zipCode}
        </Typography>
      </Stack>
    </Stack>
  )
}

export default CarrierQuotesHeader

